import { useEffect, useLayoutEffect } from 'react';
import setButtonPosition from '../utils/scripts/setButtonPosition';
import useOnboardingLoadTracker from './useOnboardingLoadTracker';

const initHeight = window.visualViewport!.height;

function setHeight() {
  const textHeight = 'px';
  const vp = window.visualViewport!;
  const htmlEl = document.querySelector('html');
  if (htmlEl) {
    htmlEl.style.height = `${vp.height}${textHeight}`;
  }
  document.documentElement.style.setProperty('--viewportHeight', String(vp.height));
  const stepContentWrapper = document.querySelector<HTMLElement>('.stepContentWrapper');
  if (stepContentWrapper) {
    stepContentWrapper.style.minHeight = `${vp.height - 82}${textHeight}`;
  }
  const application = document.querySelector<HTMLElement>('.application');
  if (application) {
    application.style.minHeight = `${vp.height}${textHeight}`;
  }

  if (document.querySelector('.bottomBar')) {
    const arrBottomBar = document.querySelectorAll<HTMLElement>('.bottomBar');
    if (arrBottomBar) {
      if (initHeight > vp.height) {
        for (let i = 0; i < arrBottomBar.length; i += 1) {
          arrBottomBar[i].style.top = `${vp.height - 82}${textHeight}`;
        }
      } else {
        for (let i = 0; i < arrBottomBar.length; i += 1) {
          arrBottomBar[i].style.top = `${vp.height - 82}${textHeight}`;
        }
      }
    }
  }
}

export default function useAppLifecycle() {
  // 1) Clear `bags` localStorage flag on mount.
  useEffect(() => {
    localStorage.removeItem('bags');
  }, []);

  // 2) Onboarding load-time analytics.
  useOnboardingLoadTracker();

  // 3) Re-sync viewport-derived DOM/CSS vars before paint on each render (covers
  //    newly mounted step DOM). Replaces the old `setInterval(setHeight, 2)` that
  //    leaked a new interval every render and thrashed the main thread (forced reflow
  //    ~500×/s). useLayoutEffect keeps the pre-paint timing, so no layout shift.
  useLayoutEffect(() => {
    setHeight();
  });

  // 4) Re-apply on real viewport changes (mobile address bar show/hide, rotation,
  //    keyboard) via visualViewport/resize events, rAF-throttled — instead of polling.
  //    Android small-screen button positioning uses the same mechanism.
  useEffect(() => {
    const vv = window.visualViewport;
    let heightRaf = 0;
    const onViewportChange = () => {
      cancelAnimationFrame(heightRaf);
      heightRaf = requestAnimationFrame(setHeight);
    };
    vv?.addEventListener('resize', onViewportChange);
    vv?.addEventListener('scroll', onViewportChange);
    window.addEventListener('resize', onViewportChange);

    const isAndroidSmall = window.innerWidth < 566 && /Android/i.test(navigator.userAgent);
    let buttonRaf = 0;
    const onButtonReposition = () => {
      cancelAnimationFrame(buttonRaf);
      buttonRaf = requestAnimationFrame(setButtonPosition);
    };
    if (isAndroidSmall) {
      setButtonPosition();
      vv?.addEventListener('resize', onButtonReposition);
      window.addEventListener('resize', onButtonReposition);
    }

    return () => {
      cancelAnimationFrame(heightRaf);
      cancelAnimationFrame(buttonRaf);
      vv?.removeEventListener('resize', onViewportChange);
      vv?.removeEventListener('scroll', onViewportChange);
      window.removeEventListener('resize', onViewportChange);
      if (isAndroidSmall) {
        vv?.removeEventListener('resize', onButtonReposition);
        window.removeEventListener('resize', onButtonReposition);
      }
    };
  }, []);

  // 5) Suppress multi-touch + gestures and seed viewport sessionStorage.
  useEffect(() => {
    document.addEventListener('touchmove', (event) => {
      if (event.touches.length > 1) {
        event.preventDefault();
      }
    }, { passive: false });

    document.addEventListener('gesturestart', (event) => {
      event.preventDefault();
    });
    document.addEventListener('gesturechange', (event) => {
      event.preventDefault();
    });
    document.addEventListener('gestureend', (event) => {
      event.preventDefault();
    });

    const initHeightValue = sessionStorage.getItem('initHeightValue');
    const viewportHeightValue = sessionStorage.getItem('viewportHeightValue');
    const reload = sessionStorage.getItem('reload');
    window.initHeight = window.visualViewport!.height;

    if (!initHeightValue) {
      sessionStorage.setItem('initHeightValue', String(window.visualViewport!.height));
    }

    if (!viewportHeightValue) {
      sessionStorage.setItem('viewportHeightValue', String(window.visualViewport!.height));
    }

    if (viewportHeightValue && initHeightValue && !reload) {
      setTimeout(() => {
        if (Number(viewportHeightValue) !== window.visualViewport!.height) {
          sessionStorage.setItem('viewportHeightValue', initHeightValue);
          sessionStorage.setItem('reload', 'true');
        }
      }, 1000);
    }
  }, []);

  // 6) Reset `reload` sessionStorage flag on user pull-down/up touch.
  useEffect(() => {
    let startY = 0;
    const reload = sessionStorage.getItem('reload');

    const handleTouchStart = (event: TouchEvent) => {
      startY = event.touches[0].clientY;
    };

    const handleTouchMove = (event: TouchEvent) => {
      const currentY = event.touches[0].clientY;

      if (currentY > startY) {
        sessionStorage.setItem('reload', '');
      } else if (currentY < startY && reload) {
        sessionStorage.setItem('reload', '');
      }
    };

    window.addEventListener('touchstart', handleTouchStart);
    window.addEventListener('touchmove', handleTouchMove);

    return () => {
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchmove', handleTouchMove);
    };
  }, []);
}
